import React, { FC, useState, useEffect, startTransition } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { Save, ArrowBack, Info } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { DeploymentUpdate, DeploymentAdminDetail, DeploymentAdminListDetail } from 'src/api/Api';
import { formatDate } from 'src/util/date-utils';

const DeploymentCard = ({ deployment }: { deployment: DeploymentAdminDetail }) => (
  <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #eee' }}>
    <Typography variant="h6" gutterBottom>Deployment Details</Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Device:</strong> {deployment.device.name}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Location:</strong> {deployment.location.location_name}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Created At:</strong> {formatDate(deployment.created_at)}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Total Files:</strong> {deployment.total_files}</Typography></Grid>
      <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Analyzed Files:</strong> {deployment.total_inferences}</Typography></Grid>
    </Grid>
  </Paper>
);

const EditDeploymentPage: FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();
  const { deployments } = state.admin;

  const [formData, setFormData] = useState<DeploymentUpdate>({});
  const [deploymentToEdit, setDeploymentToEdit] = useState<DeploymentAdminDetail | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadDeploymentData = async () => {
      if (!deploymentId) {
        showSnackbar({ message: 'No deployment ID provided.', type: snackbarType.ERROR });
        navigate(-1);
        return;
      }

      const deploymentFromState = deployments.find(d => d.deployment_id === deploymentId);

      // Use data from state for soft-nav, but still fetch full details
      if (deploymentFromState) {
        setFormData({
          deployment_name: deploymentFromState.deployment_name,
          start_date: deploymentFromState.start_date ? new Date(deploymentFromState.start_date).toISOString().split('T')[0] : '',
          end_date: deploymentFromState.end_date ? new Date(deploymentFromState.end_date).toISOString().split('T')[0] : '',
        });
        setLoading(false); // Form is usable with partial data
      } else {
        setLoading(true); // No data yet, show main loader
      }

      try {
        const response = await clientService.api.getDeploymentDetailsAdminApiV1AdminDeploymentsDeploymentIdGet(deploymentId);
        const fullDeployment = response.data.deployment;
        setDeploymentToEdit(fullDeployment);
        setFormData({
          deployment_name: fullDeployment.deployment_name,
          start_date: fullDeployment.start_date ? new Date(fullDeployment.start_date).toISOString().split('T')[0] : '',
          end_date: fullDeployment.end_date ? new Date(fullDeployment.end_date).toISOString().split('T')[0] : '',
          data_upload_status: fullDeployment.data_upload_status,
        });
      } catch (error) {
        showSnackbar({ message: 'Failed to fetch deployment details', type: snackbarType.ERROR });
        navigate(-1); // Go back if we can't fetch details
      } finally {
        setLoading(false);
      }
    };

    loadDeploymentData();
  }, [deploymentId, navigate, showSnackbar, snackbarType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.deployment_name?.trim()) {
      showSnackbar({ message: 'Please fill in all required fields.', type: snackbarType.ERROR });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!deploymentId) return;

      const payload: DeploymentUpdate = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString().split('T')[0] : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString().split('T')[0] : null,
      };

      await clientService.api.updateApiDeploymentApiV1AdminDeploymentsDeploymentIdPut(deploymentId, payload);
      showSnackbar({ message: 'Deployment updated successfully!', type: snackbarType.SUCCESS });

      const updatedDeployments = deployments.map((dep: DeploymentAdminListDetail) => {
        if (dep.deployment_id === deploymentId) {
          return {
            ...dep,
            deployment_name: payload.deployment_name || dep.deployment_name,
            start_date: payload.start_date,
            end_date: payload.end_date,
          };
        }
        return dep;
      });

      startTransition(() => {
        dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: updatedDeployments as DeploymentAdminListDetail[] });
        navigate(-1); // Go back to the previous page
      });
    } catch (error: any) {
      const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Failed to update deployment.';
      showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    startTransition(() => {
      navigate(-1);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleCancel} color="primary" sx={{ mr: 1 }} aria-label="Back" disabled={isSubmitting}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1">Edit Deployment</Typography>
        </Box>

        {deploymentToEdit ? <DeploymentCard deployment={deploymentToEdit} /> : <Box mb={3}>
          <Alert icon={<Info fontSize="inherit" />} severity="warning">
            Hold on ! Getting deployment details
          </Alert>
        </Box>}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Deployment Name"
              name="deployment_name"
              value={formData.deployment_name || ''}
              onChange={handleChange}
              required
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              name="start_date"
              type="date"
              value={formData.start_date || ''}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              name="end_date"
              type="date"
              value={formData.end_date || ''}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.data_upload_status || false}
                  onChange={handleChange}
                  name="data_upload_status"
                  disabled={isSubmitting}
                />
              }
              label="Data Upload Status"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
          <Button onClick={handleCancel} color="inherit" disabled={isSubmitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditDeploymentPage;
